import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spiner } from '../../components';
import { dedupeRoleOptions, type InviteRoleLike } from '../../utils/inviteRoleOptions';

export type DeleteRoleStrategy = 'reassign' | 'remove';

interface DeleteRoleModalProps {
    modalId?: string;
    roleLabel: string;
    availableRoles?: ReadonlyArray<InviteRoleLike> | null;
    isOpen?: boolean;
    onClose?: () => void;
    onConfirm: (payload: {
        strategy: DeleteRoleStrategy;
        targetRole?: string;
    }) => Promise<boolean | void> | boolean | void;
    mappingRegenHint?: { estimatedCredits: number; creditsRemaining?: number } | null;
    hasAssignees?: boolean;
}

/**
 * Dark Bootstrap confirmation modal when deleting a process role.
 */
const DeleteRoleModal: React.FC<DeleteRoleModalProps> = ({
    modalId = 'deleteRoleModal',
    roleLabel,
    availableRoles,
    isOpen,
    onClose,
    onConfirm,
    mappingRegenHint,
    hasAssignees = true,
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const [strategy, setStrategy] = useState<DeleteRoleStrategy>('reassign');
    const [targetRole, setTargetRole] = useState('');
    const [isSubmit, setIsSubmit] = useState(false);
    const [error, setError] = useState('');

    const otherRoles = useMemo(
        () =>
            dedupeRoleOptions(availableRoles)
                .map((o) => o.role)
                .filter((r) => r.trim().toLowerCase() !== roleLabel.trim().toLowerCase()),
        [availableRoles, roleLabel]
    );

    useEffect(() => {
        if (isOpen) {
            setStrategy(hasAssignees ? 'reassign' : 'remove');
            setTargetRole('');
            setError('');
            setIsSubmit(false);
            const timer = window.setTimeout(() => {
                const el = document.getElementById(modalId);
                if (!el) return;
                const Modal = (window as any).bootstrap?.Modal;
                if (Modal) {
                    Modal.getOrCreateInstance(el).show();
                } else {
                    const btn = document.createElement('button');
                    btn.setAttribute('data-bs-toggle', 'modal');
                    btn.setAttribute('data-bs-target', `#${modalId}`);
                    document.body.appendChild(btn);
                    btn.click();
                    btn.remove();
                }
            }, 30);
            return () => window.clearTimeout(timer);
        }
        return undefined;
    }, [isOpen, roleLabel, modalId, hasAssignees]);

    useEffect(() => {
        const el = document.getElementById(modalId);
        if (!el) return;
        const onHidden = () => {
            setError('');
            setIsSubmit(false);
            onClose?.();
        };
        el.addEventListener('hidden.bs.modal', onHidden);
        return () => el.removeEventListener('hidden.bs.modal', onHidden);
    }, [modalId, onClose]);

    const handleSubmit = async () => {
        const effectiveStrategy: DeleteRoleStrategy = hasAssignees ? strategy : 'remove';
        if (effectiveStrategy === 'reassign' && !targetRole.trim()) {
            setError(t('views.processInterview.reassignTargetRequired', 'Please choose a target role.'));
            return;
        }
        setIsSubmit(true);
        setError('');
        try {
            const result = await onConfirm({
                strategy: effectiveStrategy,
                targetRole: effectiveStrategy === 'reassign' ? targetRole.trim() : undefined,
            });
            if (result === false) {
                setIsSubmit(false);
                return;
            }
            closeRef.current?.click();
        } catch {
            setError(t('views.processInterview.roleUpdateFailed', 'Could not update role.'));
        }
        setIsSubmit(false);
    };

    return (
        <div className="modal" data-bs-backdrop="static" id={modalId} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content bg-dark">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">
                            {t('views.processInterview.deleteRoleTitle', 'Delete role')}
                        </h5>
                        <button
                            type="button"
                            className="btn-close bg-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        />
                    </div>
                    <div className="modal-body">
                        {error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : null}
                        {hasAssignees ? (
                            <>
                                <p className="text-theme mb-3">
                                    {t('views.processInterview.deleteRoleConfirm', {
                                        role: roleLabel,
                                        defaultValue: `What should happen to people assigned to “{{role}}”?`,
                                    })}
                                </p>
                                <div className="form-check mb-2">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="deleteRoleStrategy"
                                        id={`${modalId}-reassign`}
                                        checked={strategy === 'reassign'}
                                        onChange={() => setStrategy('reassign')}
                                    />
                                    <label className="form-check-label text-theme" htmlFor={`${modalId}-reassign`}>
                                        {t('views.processInterview.reassignTo', 'Reassign to another role')}
                                    </label>
                                </div>
                                {strategy === 'reassign' && (
                                    <select
                                        className="form-select bg-dark text-theme mb-3"
                                        value={targetRole}
                                        onChange={(e) => setTargetRole(e.target.value)}
                                    >
                                        <option value="">{t('navigation.selectRole')}</option>
                                        {otherRoles.map((r) => (
                                            <option key={r} value={r}>
                                                {r}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="deleteRoleStrategy"
                                        id={`${modalId}-remove`}
                                        checked={strategy === 'remove'}
                                        onChange={() => setStrategy('remove')}
                                    />
                                    <label className="form-check-label text-theme" htmlFor={`${modalId}-remove`}>
                                        {t(
                                            'views.processInterview.removeAssignments',
                                            'Remove all people from this role'
                                        )}
                                    </label>
                                </div>
                                {strategy === 'remove' && mappingRegenHint ? (
                                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                                        {t('views.processInterview.deleteMappingSourceRoleWarning', {
                                            credits: mappingRegenHint.estimatedCredits,
                                        })}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <p className="text-theme mb-0">
                                {t('views.processInterview.deleteEmptyRoleConfirm', {
                                    role: roleLabel,
                                    defaultValue: `Delete the role “{{role}}”? No one is assigned to it anymore.`,
                                })}
                            </p>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                        >
                            {t('words.close')}
                        </button>
                        <button type="button" className="btn btn-primary" onClick={() => void handleSubmit()}>
                            {isSubmit ? (
                                <Spiner className="spinner-border-sm" />
                            ) : hasAssignees && strategy === 'remove' && mappingRegenHint ? (
                                t('views.processInterview.deleteAndRegenerate', 'Delete and regenerate')
                            ) : (
                                t('words.confirm')
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteRoleModal;
